import type { FieldDefinition } from "@/api/types";

interface Props {
  field: FieldDefinition;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}

const inputType: Partial<Record<FieldDefinition["field_type"], string>> = {
  text: "text", number: "number", date: "date", email: "email", url: "url", phone: "tel",
};

/** Renders one tenant-defined field from its metadata. New field types are added here only. */
export function CustomFieldInput({ field, value, error, onChange }: Props) {
  const id = `custom-${field.key}`;
  const label = (
    <label htmlFor={id} className="label">
      {field.label}{field.required && <span className="text-danger"> *</span>}
    </label>
  );
  let control;
  switch (field.field_type) {
    case "long_text":
      control = <textarea id={id} className="input min-h-20" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)} />;
      break;
    case "boolean":
      return (
        <div className="flex items-center gap-2 pt-6">
          <input id={id} type="checkbox" className="h-4 w-4 accent-brand" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <label htmlFor={id} className="text-sm font-medium">{field.label}</label>
          {error && <p className="field-error">{error}</p>}
        </div>
      );
    case "picklist":
      control = (
        <select id={id} className="input" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Select…</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      break;
    case "multi_picklist": {
      const selected = new Set((value as string[] | undefined) ?? []);
      control = (
        <div id={id} className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <label key={o} className={`cursor-pointer rounded-full border px-3 py-1 text-xs ${selected.has(o) ? "border-brand bg-brand-soft text-brand" : "border-line"}`}>
              <input type="checkbox" className="sr-only" checked={selected.has(o)}
                onChange={() => {
                  if (selected.has(o)) selected.delete(o);
                  else selected.add(o);
                  onChange([...selected]);
                }} />
              {o}
            </label>
          ))}
        </div>
      );
      break;
    }
    default:
      control = (
        <input id={id} className="input" type={inputType[field.field_type] ?? "text"} value={(value as string | number | undefined) ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? null : field.field_type === "number" ? Number(raw) : raw);
          }} />
      );
  }
  return (
    <div>
      {label}
      {control}
      {field.help_text && !error && <p className="mt-1 text-xs text-muted">{field.help_text}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
